import React, { useEffect, useMemo, useState } from "react";
import {
  adminListUsers,
  adminToggleUserStatus,
  adminResetPassword,
  adminGetAudit,
  adminCreateUser,
  adminUpdateRole,
  adminResendInvite,
} from "./api";
import CamposGestao from "./gestao/CamposGestao";

export default function AdminPanelPage({ user }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(""); // "", "ativo", "inativo"
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [audit, setAudit] = useState([]);

  // ===== UI: Toast =====
  const [toast, setToast] = useState(null);
  // toast = { type: "success"|"error"|"info", title, message }

  function showToast(payload) {
    setToast(payload);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 3800);
  }

  // ===== UI: Confirm Modal =====
  const [confirm, setConfirm] = useState(null);
  // confirm = { title, message, confirmText, danger, onConfirm }

  function openConfirm({ title, message, confirmText = "Confirmar", danger = false, onConfirm }) {
    setConfirm({ title, message, confirmText, danger, onConfirm });
  }
  function closeConfirm() {
    setConfirm(null);
  }

  // ===== UI: Modal "Role / Acesso" =====
  const [roleModal, setRoleModal] = useState(null);
  // roleModal = { user, role }
  const [roleSaving, setRoleSaving] = useState(false);

  // ===== UI: Modal Criar Usuário =====
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createForm, setCreateForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "SUPORTE", // SUPORTE | ADM | USER
  });
  const [existingUserHint, setExistingUserHint] = useState(null);

  const groups = useMemo(() => user?.groups || [], [user]);

  const canSeeAdmin = useMemo(() => {
    return Boolean(user?.is_superuser || groups.includes("ADM") || groups.includes("SUPORTE"));
  }, [user, groups]);

  const canFullAdmin = useMemo(() => {
    return Boolean(user?.is_superuser || groups.includes("ADM"));
  }, [user, groups]);

  const canSupport = useMemo(() => {
    return Boolean(user?.is_superuser || groups.includes("ADM") || groups.includes("SUPORTE"));
  }, [user, groups]);

  const canCreateUsers = canFullAdmin; // só ADM/SUPER
  const canEditRole = canFullAdmin; // só ADM/SUPER

  async function loadUsers() {
    setLoading(true);
    try {
      const data = await adminListUsers({
        q: q || "",
        status: status || "",
      });
      const arr = data?.results || data || [];
      setUsers(Array.isArray(arr) ? arr : []);
    } catch (err) {
      showToast({
        type: "error",
        title: "Erro",
        message: err?.message || "Erro ao carregar usuários.",
      });
    } finally {
      setLoading(false);
    }
  }

  function handleToggleActive(u) {
    openConfirm({
      title: u.is_active ? "Desativar usuário?" : "Ativar usuário?",
      message: `Confirma ${u.is_active ? "desativar" : "ativar"} o usuário:\n${u.email}`,
      confirmText: u.is_active ? "Desativar" : "Ativar",
      danger: Boolean(u.is_active),
      onConfirm: async () => {
        closeConfirm();
        try {
          await adminToggleUserStatus(u.id, !u.is_active);
          showToast({
            type: "success",
            title: "Sucesso",
            message: `Usuário ${u.is_active ? "desativado" : "ativado"} com sucesso.`,
          });
          await loadUsers();
        } catch (err) {
          showToast({ type: "error", title: "Erro", message: err?.message || "Falha ao alterar status." });
        }
      },
    });
  }

  function handleResetPassword(u) {
    // IMPORTANTE: isso precisa chamar endpoint REAL de reset senha (não o convite).
    openConfirm({
      title: "Reset de senha",
      message: `Enviar reset de senha para:\n${u.email}`,
      confirmText: "Enviar reset",
      danger: false,
      onConfirm: async () => {
        closeConfirm();
        try {
          const data = await adminResetPassword(u.id);

          if (data?.temp_password) {
            showToast({
              type: "info",
              title: "E-mail falhou",
              message: `Senha temporária gerada: ${data.temp_password}`,
            });
          } else {
            showToast({
              type: "success",
              title: "Reset enviado",
              message: "O e-mail de reset de senha foi enviado com sucesso.",
            });
          }
        } catch (err) {
          showToast({ type: "error", title: "Erro", message: err?.message || "Erro ao enviar reset." });
        }
      },
    });
  }

  function handleResendInvite(u) {
    // Reenviar convite = link para criar senha/ativar (fluxo de invite)
    openConfirm({
      title: "Reenviar convite",
      message: `Reenviar convite (criar senha/ativar) para:\n${u.email}`,
      confirmText: "Reenviar convite",
      danger: false,
      onConfirm: async () => {
        closeConfirm();
        try {
          await adminResendInvite(u.id);
          showToast({ type: "success", title: "Convite reenviado", message: "Convite enviado com sucesso." });
        } catch (err) {
          showToast({ type: "error", title: "Erro", message: err?.message || "Erro ao reenviar convite." });
        }
      },
    });
  }

  function openRole(u) {
    // tenta inferir role atual via groups (se backend envia) ou deixa USER
    const g = (u?.groups || []).map(String);
    const initial =
      g.includes("ADM") ? "ADM" : g.includes("SUPORTE") ? "SUPORTE" : "USER";

    setRoleModal({ user: u, role: initial });
  }

  async function saveRole() {
    if (!roleModal?.user?.id) return;
    const u = roleModal.user;
    const role = String(roleModal.role || "USER").trim();

    setRoleSaving(true);
    try {
      await adminUpdateRole(u.id, role);
      showToast({ type: "success", title: "Acesso atualizado", message: `Role de ${u.email} = ${role}` });
      setRoleModal(null);
      await loadUsers();
    } catch (err) {
      showToast({ type: "error", title: "Erro", message: err?.message || "Erro ao atualizar role." });
    } finally {
      setRoleSaving(false);
    }
  }

  async function openAudit(u) {
    setSelectedUser(u);
    setAudit([]);
    setAuditLoading(true);

    try {
      const data = await adminGetAudit(u.id);
      const arr = data?.results || data || [];
      setAudit(Array.isArray(arr) ? arr : []);
    } catch (err) {
      showToast({ type: "error", title: "Erro", message: err?.message || "Erro ao carregar histórico." });
    } finally {
      setAuditLoading(false);
    }
  }

  function closeAudit() {
    setSelectedUser(null);
    setAudit([]);
  }

  // ===== Criar Usuário =====
  function openCreate() {
    setCreateError("");
    setExistingUserHint(null);
    setCreateForm({ email: "", first_name: "", last_name: "", role: "SUPORTE" });
    setCreateOpen(true);
  }

  function closeCreate() {
    setCreateOpen(false);
    setCreateLoading(false);
    setCreateError("");
    setExistingUserHint(null);
  }

  async function submitCreate() {
    setCreateError("");
    setExistingUserHint(null);

    const email = (createForm.email || "").trim().toLowerCase();
    if (!email) {
      setCreateError("Informe um e-mail.");
      return;
    }

    setCreateLoading(true);
    try {
      const payload = {
        email,
        first_name: (createForm.first_name || "").trim(),
        last_name: (createForm.last_name || "").trim(),
        role: createForm.role,
      };

      const data = await adminCreateUser(payload);
      showToast({ type: "success", title: "Usuário criado", message: data?.email || email });

      closeCreate();
      await loadUsers();
    } catch (err) {
      const msg = String(err?.message || err || "");
      if (msg.includes("Já existe usuário com esse e-mail")) {
        setCreateError("Esse e-mail já existe. Você pode atualizar o nível de acesso (role).");
        setExistingUserHint({ email, suggestedRole: createForm.role });
      } else {
        setCreateError(msg || "Erro ao criar usuário.");
      }
    } finally {
      setCreateLoading(false);
    }
  }

  async function submitUpdateRoleExisting() {
    const email = existingUserHint?.email;
    if (!email) return;

    const found = users.find((u) => String(u.email || "").toLowerCase() === email);
    if (!found?.id) {
      setCreateError("Não encontrei esse usuário na lista atual. Clique em Atualizar e tente novamente.");
      return;
    }

    openConfirm({
      title: "Atualizar acesso",
      message: `Atualizar role de ${email} para ${existingUserHint.suggestedRole}?`,
      confirmText: "Atualizar",
      danger: false,
      onConfirm: async () => {
        closeConfirm();
        setCreateLoading(true);
        try {
          await adminUpdateRole(found.id, existingUserHint.suggestedRole);
          showToast({ type: "success", title: "Role atualizada", message: "Acesso atualizado com sucesso." });
          closeCreate();
          await loadUsers();
        } catch (err) {
          setCreateError(err?.message || "Erro ao atualizar role.");
        } finally {
          setCreateLoading(false);
        }
      },
    });
  }

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!canSeeAdmin) {
    return (
      <div style={styles.wrap}>
        <h1 style={styles.title}>Painel Admin</h1>
        <div style={styles.card}>
          <p style={{ margin: 0, opacity: 0.9 }}>
            Você não tem permissão para acessar esta área.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <style>{`
        .admin-select option {
          background: var(--surface);
          color: var(--text-primary);
        }
      `}</style>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Painel Admin</h1>
          <div style={styles.subtitle}>Gerencie usuários, status e histórico (ADM / SUPORTE)</div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {canCreateUsers ? (
            <button onClick={openCreate} style={styles.btnPrimary}>
              + Novo usuário
            </button>
          ) : null}

          <button
            onClick={loadUsers}
            disabled={loading}
            style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
          >
            {loading ? "Carregando..." : "Atualizar"}
          </button>
        </div>
      </div>

      <div style={styles.filters}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome ou e-mail..."
          style={styles.input}
        />

        <select value={status} onChange={(e) => setStatus(e.target.value)} style={styles.select} className="admin-select">
          <option value="">Status (todos)</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
        </select>

        <button
          onClick={loadUsers}
          disabled={loading}
          style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
        >
          Buscar
        </button>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Usuário</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Criado no painel</th>
              <th style={styles.th}>Ações</th>
            </tr>
          </thead>

          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={styles.tr}>
                <td style={styles.td}>
                  <div style={{ fontWeight: 800 }}>{u.nome || "-"}</div>
                  <div style={{ opacity: 0.85, fontSize: 13 }}>{u.email}</div>
                  {!!u.date_joined && <div style={{ opacity: 0.7, fontSize: 12 }}>Cadastro: {u.date_joined}</div>}
                  {!!u.activated_at && <div style={{ opacity: 0.7, fontSize: 12 }}>Ativação/Compra: {u.activated_at}</div>}
                </td>

                <td style={styles.td}>
                  <span style={{ ...styles.badge, ...(u.is_active ? styles.badgeOk : styles.badgeOff) }}>
                    {u.is_active ? "ATIVO" : "INATIVO"}
                  </span>
                </td>

                <td style={styles.td}>{u.created_by_admin ? "Sim" : "Não"}</td>

                <td style={styles.td}>
                  <div style={styles.actions}>
                    {canSupport ? (
                      <button
                        onClick={() => handleToggleActive(u)}
                        style={{
                          ...styles.btnSmall,
                          ...(u.is_active ? styles.btnDanger : styles.btnOk),
                        }}
                      >
                        {u.is_active ? "Desativar" : "Ativar"}
                      </button>
                    ) : null}

                    {canSupport ? (
                      <button onClick={() => handleResetPassword(u)} style={styles.btnSmall}>
                        Reset senha
                      </button>
                    ) : null}

                    {canSupport ? (
                      <button onClick={() => handleResendInvite(u)} style={styles.btnSmall}>
                        Reenviar convite
                      </button>
                    ) : null}

                    {canSupport ? (
                      <button onClick={() => openAudit(u)} style={styles.btnSmall}>
                        Histórico
                      </button>
                    ) : null}

                    {canEditRole ? (
                      <button onClick={() => openRole(u)} style={{ ...styles.btnSmall, ...styles.btnMuted }}>
                        Acesso (role)
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}

            {users.length === 0 && (
              <tr>
                <td colSpan="5" style={{ ...styles.td, padding: 16, opacity: 0.85 }}>
                  Nenhum usuário encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CamposGestao />

      {/* ===== Modal Criar Usuário ===== */}
      {createOpen && (
        <Modal onClose={closeCreate} title="Criar usuário" subtitle="Cria usuário, define role e envia convite por e-mail.">
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            <input
              style={styles.input}
              placeholder="E-mail (obrigatório)"
              value={createForm.email}
              onChange={(e) => setCreateForm((s) => ({ ...s, email: e.target.value }))}
            />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input
                style={styles.input}
                placeholder="Nome"
                value={createForm.first_name}
                onChange={(e) => setCreateForm((s) => ({ ...s, first_name: e.target.value }))}
              />
              <input
                style={styles.input}
                placeholder="Sobrenome"
                value={createForm.last_name}
                onChange={(e) => setCreateForm((s) => ({ ...s, last_name: e.target.value }))}
              />
            </div>

            <select
              value={createForm.role}
              onChange={(e) => setCreateForm((s) => ({ ...s, role: e.target.value }))}
              style={styles.select}
              className="admin-select"
            >
              <option value="SUPORTE">SUPORTE</option>
              <option value="ADM">ADM</option>
              <option value="USER">USER</option>
            </select>

            {createError ? <div style={styles.errorBox}>{createError}</div> : null}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {existingUserHint ? (
                <button
                  onClick={submitUpdateRoleExisting}
                  disabled={createLoading}
                  style={{ ...styles.btnPrimary, opacity: createLoading ? 0.7 : 1 }}
                >
                  {createLoading ? "Processando..." : "Atualizar role do usuário existente"}
                </button>
              ) : null}

              <button
                onClick={submitCreate}
                disabled={createLoading}
                style={{ ...styles.btnPrimary, opacity: createLoading ? 0.7 : 1 }}
              >
                {createLoading ? "Criando..." : "Criar usuário"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== Modal Histórico ===== */}
      {selectedUser && (
        <Modal onClose={closeAudit} title="Histórico" subtitle={selectedUser.email}>
          <div style={{ marginTop: 12 }}>
            {auditLoading ? (
              <div style={{ opacity: 0.9 }}>Carregando histórico...</div>
            ) : audit.length === 0 ? (
              <div style={{ opacity: 0.9 }}>Sem alterações registradas.</div>
            ) : (
              audit.map((a) => (
                <div key={a.id || `${a.action}-${a.created_at}`} style={styles.auditItem}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ fontWeight: 900 }}>{a.action}</div>
                    <div style={{ opacity: 0.75, fontSize: 12 }}>{a.created_at}</div>
                  </div>

                  <div style={{ opacity: 0.9, marginTop: 6, fontSize: 13 }}>
                    <b>Quem:</b> {a.actor_email || a.actor_name || a.actor || "-"}
                  </div>

                  {(a.before || a.after) && (
                    <details style={{ marginTop: 8, opacity: 0.95 }}>
                      <summary style={{ cursor: "pointer" }}>Ver detalhes</summary>
                      <pre style={styles.pre}>
                        {JSON.stringify({ before: a.before || null, after: a.after || null }, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))
            )}
          </div>
        </Modal>
      )}

      {/* ===== Modal Role ===== */}
      {roleModal && (
        <Modal
          onClose={() => (roleSaving ? null : setRoleModal(null))}
          title="Acesso (role)"
          subtitle={roleModal.user?.email}
          actions={
            <>
              <button onClick={() => (roleSaving ? null : setRoleModal(null))} style={styles.btnSmall} disabled={roleSaving}>
                Cancelar
              </button>
              <button onClick={saveRole} style={{ ...styles.btnSmall, ...styles.btnMuted }} disabled={roleSaving}>
                {roleSaving ? "Salvando..." : "Salvar"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            <label style={styles.label}>Role</label>
            <select
              value={roleModal.role}
              onChange={(e) => setRoleModal((s) => ({ ...s, role: e.target.value }))}
              style={styles.select}
              className="admin-select"
            >
              <option value="USER">USER</option>
              <option value="SUPORTE">SUPORTE</option>
              <option value="ADM">ADM</option>
            </select>

            <div style={{ opacity: 0.75, fontSize: 12 }}>
              <b>ADM</b> = tudo. <b>SUPORTE</b> = ações limitadas. <b>USER</b> = sem painel admin.
            </div>
          </div>
        </Modal>
      )}

      {/* ===== Confirm ===== */}
      {confirm && (
        <div style={styles.modalBackdrop} onClick={closeConfirm}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 16 }}>{confirm.title}</div>
                <div style={{ opacity: 0.85, fontSize: 13, whiteSpace: "pre-line", marginTop: 6 }}>
                  {confirm.message}
                </div>
              </div>

              <button onClick={closeConfirm} style={styles.btnSmall}>
                Fechar
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
              <button onClick={closeConfirm} style={styles.btnSmall}>
                Cancelar
              </button>
              <button
                onClick={confirm.onConfirm}
                style={{
                  ...styles.btnSmall,
                  ...(confirm.danger ? styles.btnDanger : styles.btnBlue),
                }}
              >
                {confirm.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Toast ===== */}
      {toast && (
        <div style={styles.toastWrap}>
          <div style={{ ...styles.toast, ...(toast.type === "error" ? styles.toastError : toast.type === "success" ? styles.toastSuccess : styles.toastInfo) }}>
            <div style={{ fontWeight: 900 }}>{toast.title}</div>
            <div style={{ marginTop: 6, opacity: 0.92, fontSize: 13, whiteSpace: "pre-line" }}>
              {toast.message}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Modal base */
function Modal({ onClose, title, subtitle, actions, children }) {
  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16 }}>{title}</div>
            {subtitle ? <div style={{ opacity: 0.8, fontSize: 13, marginTop: 6 }}>{subtitle}</div> : null}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {actions ? actions : null}
            <button onClick={onClose} style={styles.btnSmall}>
              Fechar
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>{children}</div>
      </div>
    </div>
  );
}

const styles = {
  wrap: { padding: 8 },

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  title: { margin: 0, fontSize: 26, fontWeight: 900, letterSpacing: -0.4, color: "var(--text-primary)" },
  subtitle: { marginTop: 6, fontSize: 13, color: "var(--text-muted)" },

  filters: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: 12,
  },

  input: {
    flex: 1,
    minWidth: 240,
    padding: 10,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--input-bg)",
    color: "var(--text-primary)",
    outline: "none",
    fontFamily: "inherit",
    fontSize: 14,
  },
  select: {
    padding: 10,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--input-bg)",
    color: "var(--text-primary)",
    outline: "none",
    minWidth: 170,
    fontFamily: "inherit",
    fontSize: 14,
  },
  label: { fontSize: 13, fontWeight: 800, color: "var(--text-muted)" },

  btn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
    color: "var(--text-primary)",
    cursor: "pointer",
    fontWeight: 700,
    fontFamily: "inherit",
    fontSize: 14,
  },
  btnPrimary: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid var(--accent-border)",
    background: "var(--accent)",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: 900,
    fontFamily: "inherit",
    fontSize: 14,
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },

  tableWrap: {
    overflowX: "auto",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--surface)",
  },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 900 },
  th: {
    textAlign: "left",
    padding: 12,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-muted)",
    borderBottom: "1px solid var(--border)",
    background: "var(--surface-2)",
  },
  tr: { borderTop: "1px solid var(--border)" },
  td: { padding: 12, verticalAlign: "top", fontSize: 14, color: "var(--text-primary)" },

  badge: {
    display: "inline-flex",
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    border: "1px solid var(--border)",
    color: "var(--text-primary)",
  },
  badgeOk:  { background: "var(--success-bg)",  border: "1px solid var(--success-border)", color: "var(--success)" },
  badgeOff: { background: "var(--error-bg)",    border: "1px solid var(--error-border)",   color: "var(--error)" },

  actions: { display: "flex", gap: 8, flexWrap: "wrap" },

  btnSmall: {
    padding: "7px 10px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "var(--surface-2)",
    color: "var(--text-primary)",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 12,
    fontFamily: "inherit",
  },
  btnOk:    { background: "var(--success-bg)",       border: "1px solid var(--success-border)", color: "var(--success)" },
  btnDanger:{ background: "var(--error-bg)",         border: "1px solid var(--error-border)",   color: "var(--error)" },
  btnBlue:  { background: "var(--accent-dim)",       border: "1px solid var(--accent-border)",  color: "var(--accent)" },
  btnMuted: { background: "var(--surface-2)",        border: "1px solid var(--border)",         color: "var(--text-muted)" },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(26, 31, 29, 0.45)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 9999,
  },
  modal: {
    width: "min(620px, 95vw)",
    maxHeight: "80vh",
    overflow: "auto",
    borderRadius: 14,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    padding: 20,
    boxShadow: "0 8px 32px rgba(26, 31, 29, 0.12)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    borderBottom: "1px solid var(--border)",
    paddingBottom: 12,
    marginBottom: 12,
  },

  auditItem: {
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    background: "var(--surface-2)",
  },
  pre: {
    marginTop: 8,
    padding: 10,
    borderRadius: 8,
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    overflowX: "auto",
    fontSize: 12,
    color: "var(--text-primary)",
  },
  errorBox: {
    padding: 10,
    borderRadius: 10,
    border: "1px solid var(--error-border)",
    background: "var(--error-bg)",
    color: "var(--error)",
    fontSize: 13,
  },

  toastWrap: {
    position: "fixed",
    right: 16,
    bottom: 16,
    zIndex: 10000,
  },
  toast: {
    width: "min(360px, 92vw)",
    borderRadius: 12,
    padding: 14,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    boxShadow: "0 4px 16px rgba(26, 31, 29, 0.10)",
    color: "var(--text-primary)",
    fontSize: 14,
  },
  toastSuccess: { borderColor: "var(--success-border)", background: "var(--success-bg)", color: "var(--success)" },
  toastError:   { borderColor: "var(--error-border)",   background: "var(--error-bg)",   color: "var(--error)" },
  toastInfo:    { borderColor: "var(--accent-border)",  background: "var(--accent-dim)", color: "var(--accent)" },

  card: {
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    padding: 16,
  },
};